import { ReactNode, useCallback, useState } from "react";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    ScheduleBulkBillingTarget,
    ScheduleMarkBilledBulkModal,
} from "./ScheduleMarkBilledBulkModal";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";
import {
    getSingleScheduleEntityId,
    getUniqueScheduleApplicationIds,
} from "./scheduleApplicationRowId";
import { SCHEDULE_MARK_BILLED_BULK_LIMIT } from "@common/schemas/domains/accounting/scheduleBilling.schema";
import {
    SCHEDULE_BULK_BILLING_DISABLED_REASON,
    SCHEDULE_BULK_BILLING_FULLY_BILLED_REASON,
    SCHEDULE_BULK_BILLING_LIMIT_REASON,
    SCHEDULE_BULK_BILLING_NOTHING_TO_BILL_REASON,
    SCHEDULE_BULK_BILLING_ONE_ENTITY_REASON,
} from "./scheduleBillingCopy";
import {
    canRowMarkBilled,
    hasRowNothingToBillToday,
    isRowBillableAccruedSchedule,
} from "./scheduleBillingRowHelpers";

export function getDisabledReason(
    selectedRows: ScheduleApplicationParentRow[],
): string | undefined {
    if (
        getUniqueScheduleApplicationIds(selectedRows).length >
        SCHEDULE_MARK_BILLED_BULK_LIMIT
    ) {
        return SCHEDULE_BULK_BILLING_LIMIT_REASON;
    }
    if (!selectedRows.every(isRowBillableAccruedSchedule)) {
        return SCHEDULE_BULK_BILLING_DISABLED_REASON;
    }
    if (!selectedRows.every(canRowMarkBilled)) {
        return SCHEDULE_BULK_BILLING_FULLY_BILLED_REASON;
    }
    if (selectedRows.some(hasRowNothingToBillToday)) {
        return SCHEDULE_BULK_BILLING_NOTHING_TO_BILL_REASON;
    }
    return selectedRows.length > 1 &&
        getSingleScheduleEntityId(selectedRows) === null
        ? SCHEDULE_BULK_BILLING_ONE_ENTITY_REASON
        : undefined;
}

/** "Mark as billed" for the schedules list bulk actions, plus its modal. */
export function useScheduleApplicationsBulkBilling(options: {
    selectedRows: ScheduleApplicationParentRow[];
    onCompleted: () => void;
}): {
    onMarkBilled: () => void;
    markBilledDisabledReason: string | undefined;
    modal: ReactNode;
} {
    const { selectedRows, onCompleted } = options;
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const [target, setTarget] = useState<ScheduleBulkBillingTarget | null>(
        null,
    );

    const onMarkBilled = useCallback(() => {
        const [first] = selectedRows;
        if (!first || refuseIfViewOnly()) {
            return;
        }
        setTarget({
            applicationIds: getUniqueScheduleApplicationIds(selectedRows),
            entityId: first.entityId,
        });
    }, [refuseIfViewOnly, selectedRows]);

    return {
        onMarkBilled,
        markBilledDisabledReason: getDisabledReason(selectedRows),
        modal: (
            <ScheduleMarkBilledBulkModal
                target={target}
                onHide={() => setTarget(null)}
                onCompleted={onCompleted}
            />
        ),
    };
}
